// Shared task filter bar used across Kanban, Sprints, Epic, Graph views.
import React, { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Search, X, Filter as FilterIcon } from 'lucide-react';
import { useWorkAreas } from '../../contexts/WorkAreaContext';
import { tasksApi, STATUS_LIST, PRIORITY_LABELS } from './tasksApi';
import { cn } from '../../lib/utils';

export const TaskFilterBar = ({ value, onChange, showStatus = true, compact = false }) => {
  const { workAreaNames } = useWorkAreas();
  const [people, setPeople] = useState([]);
  const [bpItems, setBpItems] = useState([]); // sprints + epics + projects + goals

  useEffect(() => {
    tasksApi.listPeople().then(setPeople).catch(() => {});
    Promise.all([tasksApi.listSprints(), tasksApi.listEpics(), tasksApi.listProjects(), tasksApi.listGoals()])
      .then(([s, e, p, g]) => setBpItems([...(s || []), ...(e || []), ...(p || []), ...(g || [])]))
      .catch(() => {});
  }, []);

  const update = (patch) => onChange({ ...value, ...patch });

  const toggleSet = (setName, item) => {
    const cur = new Set(value[setName] || new Set());
    if (cur.has(item)) cur.delete(item); else cur.add(item);
    update({ [setName]: cur });
  };

  const clear = () => onChange({
    search: '', statuses: new Set(), priorities: new Set(),
    work_area_ids: new Set(), person_ids: new Set(), big_picture_ids: new Set(),
    dateFrom: '', dateTo: '',
  });

  const activeCount =
    (value.statuses?.size || 0) + (value.priorities?.size || 0) + (value.work_area_ids?.size || 0) +
    (value.person_ids?.size || 0) + (value.big_picture_ids?.size || 0) +
    (value.dateFrom ? 1 : 0) + (value.dateTo ? 1 : 0);

  return (
    <div className="bg-card border border-border rounded-md p-2 mb-3" data-testid="task-filter-bar">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            value={value.search || ''}
            onChange={(e) => update({ search: e.target.value })}
            placeholder="Search tasks…"
            className="pl-7 h-8 text-xs"
            data-testid="task-filter-search"
          />
        </div>
        <Input
          type="date"
          value={value.dateFrom || ''}
          onChange={(e) => update({ dateFrom: e.target.value })}
          className="h-8 text-xs w-[140px]"
          data-testid="task-filter-date-from"
        />
        <Input
          type="date"
          value={value.dateTo || ''}
          onChange={(e) => update({ dateTo: e.target.value })}
          className="h-8 text-xs w-[140px]"
          data-testid="task-filter-date-to"
        />
        {activeCount > 0 && (
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={clear} data-testid="task-filter-clear">
            <X className="w-3 h-3 mr-1" /> Clear ({activeCount})
          </Button>
        )}
      </div>

      {!compact && (
        <div className="mt-2 flex flex-wrap gap-3 text-xs">
          {showStatus && (
            <FilterChips label="Status" testid="filter-status">
              {STATUS_LIST.map((s) => (
                <Chip key={s} active={value.statuses?.has(s)} onClick={() => toggleSet('statuses', s)}
                  testid={`filter-status-${s}`}>{s}</Chip>
              ))}
            </FilterChips>
          )}
          <FilterChips label="Priority" testid="filter-priority">
            {[0, 1, 2, 3].map((p) => (
              <Chip key={p} active={value.priorities?.has(p)} onClick={() => toggleSet('priorities', p)}
                testid={`filter-priority-${p}`}>{PRIORITY_LABELS[p]}</Chip>
            ))}
          </FilterChips>
          <FilterChips label="Work area" testid="filter-work-area">
            {Object.entries(workAreaNames).slice(0, 12).map(([id, name]) => (
              <Chip key={id} active={value.work_area_ids?.has(id)} onClick={() => toggleSet('work_area_ids', id)}
                testid={`filter-wa-${id}`}>{name}</Chip>
            ))}
          </FilterChips>
          {bpItems.length > 0 && (
            <FilterChips label="Project / Sprint / Epic" testid="filter-bp">
              {bpItems.slice(0, 10).map((b) => (
                <Chip key={b.item_id} active={value.big_picture_ids?.has(b.item_id)}
                  onClick={() => toggleSet('big_picture_ids', b.item_id)}
                  testid={`filter-bp-${b.item_id}`}>
                  <span className="text-[9px] mr-1 opacity-70">{b.type}</span>{b.title}
                </Chip>
              ))}
            </FilterChips>
          )}
          {people.length > 0 && (
            <FilterChips label="Person" testid="filter-person">
              {people.slice(0, 10).map((p) => (
                <Chip key={p.user_id || p.person_id || p.email}
                  active={value.person_ids?.has(p.user_id || p.person_id)}
                  onClick={() => toggleSet('person_ids', p.user_id || p.person_id)}
                  testid={`filter-person-${p.user_id || p.person_id}`}>
                  {p.name || p.email}
                </Chip>
              ))}
            </FilterChips>
          )}
        </div>
      )}
    </div>
  );
};

const FilterChips = ({ label, children, testid }) => (
  <div className="flex items-center gap-1 flex-wrap" data-testid={testid}>
    <span className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wider mr-1">{label}</span>
    {children}
  </div>
);

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={cn(
      'h-6 px-2 rounded text-[10px] border transition-colors',
      active
        ? 'bg-violet-500 text-white border-violet-500'
        : 'bg-background text-muted-foreground border-border hover:border-violet-500/50'
    )}>
    {children}
  </button>
);

export const emptyFilter = () => ({
  search: '',
  statuses: new Set(),
  priorities: new Set(),
  work_area_ids: new Set(),
  person_ids: new Set(),
  big_picture_ids: new Set(),
  dateFrom: '',
  dateTo: '',
});

export default TaskFilterBar;
